import React from 'react';
import { motion } from 'framer-motion';
import { Star, Mic, Heart, Target, CheckCircle, ArrowLeft, Lightbulb, XCircle, CheckSquare, Zap, Search, Send, HelpCircle, FileText, Video, ExternalLink } from 'lucide-react';
import { StepCard, VideoCard, VideoGrid, InfoCard } from './SectionComponents';

const MarketingStandOutSection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {
  
  const elevatorPitchExample = `"I started out in content marketing, but quickly found myself gravitating toward growth-focused roles where I could test, learn and iterate fast. At my last startup, I led the end-to-end launch of three paid acquisition campaigns that reduced our CAC by over 30% in under two months. I've worked across Meta, Google and TikTok – always trying to find that balance between creative storytelling and performance. Outside of work, I run a small community for marketers experimenting with AI tools, and I also write a monthly newsletter on behavioral psychology. I'm drawn to this role because I want to be in an environment where creativity moves the needle – and where I can work across the full funnel."`;

  const elevatorPitchElements = [
    "Who you are professionally – mention your background and your most relevant 1-2 experiences",
    "Key achievements – what are you most proud of?",
    "A personal touch – e.g. a hobby, side project or fun fact that makes you memorable",
    "Why you're here – show how your path has led you to wanting this kind of role at this kind of startup"
  ];

  const elevatorPitchExamples = [
    {
      name: "Annabel – Founder Associate @ Mast",
      url: "https://www.loom.com/share/94deaa38f3244496b557c296d09bd0c0?sid=8e3e94cf-c97a-49b3-98d8-b0a83b02b72f",
      creator: "Watch how she connects her background to the startup mission"
    },
    {
      name: "Kabir – Founder Associate @ Autogen AI", 
      url: "https://www.loom.com/share/e142d4b0baec4289984f34827d4bdb9f?sid=a039ce89-ae73-4e77-97b8-a26f4fae3fef",
      creator: "See how he demonstrates enthusiasm and relevant experience"
    },
    {
      name: "Cordi – Founder Associate @ ESG360",
      url: "https://www.loom.com/share/882393fd6adc4532b7737df570e1d3b4?sid=56a2dcc0-d2c1-4a8e-b504-181312118d81",
      creator: "Notice her personal touch and clear motivation"
    }
  ];

  const standoutTips = [
    {
      icon: Search,
      title: "Show you've done deep research",
      description: "Reference specific blog posts, product updates or LinkedIn comments from the team. Mention competitors, market trends or customer insights.",
      examples: [
        "Reference a specific blog post, product update or LinkedIn comment from the team",
        "Mention a competitor, market trend or customer insight", 
        "Suggest something small they could improve (tactfully)"
      ]
    },
    {
      icon: Lightbulb,
      title: "Arrive with real ideas",
      description: "Treat the interview like you've already started the job by coming prepared with ideas tailored to the role and stage of the company.",
      examples: [
        "Propose content themes, SEO improvements or event ideas",
        "Suggest low-cost ways to grow awareness or community",
        "Come with 2-3 rough ideas for improving positioning or tone of voice"
      ]
    },
    {
      icon: FileText,
      title: "Structure your answers clearly",
      description: "Make clear statements backed up with tangible examples.",
      examples: [
        "Make a statement – e.g. I am good at dealing with pressure",
        "Back up that statement with 1-2 clear, tangible examples of how you demonstrated this skill"
      ]
    }
  ];

  const companyObsessionTips = [
    "Mention a specific product, feature, blog post or user problem they're tackling",
    "Talk about how your background connects to their stage, mission or challenges", 
    "Highlight how you'd add value to where they are now, not just why they're cool",
    "Mention a customer persona and how you'd approach engaging them"
  ];

  const motivationExamples = [
    {
      text: "I listened to your founder on the XYZ podcast and was drawn to how you tackled [specific problem]. It really resonated with the experience I had developing XYZ product feature at my current company where I [outline experience]"
    },
    {
      text: "I think my experience at [XYZ Company] will stand me in really good stead for hitting the ground running in this role as I've learnt so much about figuring things out as I go – testing, learning, and changing tack without slowing down. I know this 'scrappy startup' stage well: limited resources, lots of ambiguity, and the need to iterate quickly. That's where I'm most comfortable and where I've delivered before."
    }
  ];

  const jobCapabilityTips = [
    "Previous projects that mirror what you'd be doing",
    "Tools or channels you already know (or are proactively learning)",
    "Where you'd add value from day one"
  ];

  const questionTypes = [
    {
      icon: Target,
      title: "Awareness of the funnel",
      description: "Show you understand marketing funnels and customer journeys"
    },
    {
      icon: Search,
      title: "Curiosity about data and users", 
      description: "Demonstrate analytical thinking and user-focused mindset"
    },
    {
      icon: Heart,
      title: "Signal long-term interest",
      description: "Show genuine enthusiasm for the company's mission"
    }
  ];

  const badQuestions = [
    "What's your favourite thing about working here?",
    "What was the inspiration behind this startup?",
    "What do you love most about your job?",
    "What's the culture like?",
    "What are the goals for the next 1–2 years?",
    "What are your company values?",
    "What is your biggest challenge at the moment?",
    "What are the top traits you are looking for in this hire?",
    "What are the typical hours/salary?",
    "Questions that can be answered on the company website"
  ];

  const goodQuestions = [
    "From my research company X seems like the biggest threat as they recently launched feature Y – are you worried about this eating into market share or is there a plan to counter?",
    "From the video it sounds as if the plan is to raise series A, but it also looks like there are no other product verticals in your roadmap as per article Y I found online. Does that mean the goal is to scale revenue growth with new customers over the next 12 months? How will this role contribute to that?",
    "You mentioned that driving user acquisition is a top priority – can you share more about which parts of the funnel currently convert best, and where you're seeing the biggest drop-offs? I'd love to understand where this role can have the most immediate impact."
  ];

  const avoidTips = [
    {
      icon: XCircle,
      title: "Generic or templated answers",
      description: "If your answer could be used in 10 different interviews, it's too vague"
    },
    {
      icon: FileText,
      title: "CV walk-throughs", 
      description: "You're not reading your LinkedIn profile and interviewers will have already seen this!"
    },
    {
      icon: HelpCircle,
      title: "Lack of examples",
      description: "Back everything up with stories or results"
    },
    {
      icon: XCircle,
      title: "Sounding robotic",
      description: "Be confident and warm. Energy matters!"
    },
    {
      icon: Zap,
      title: "Avoid 'fluff'",
      description: "It's easy to fall back on fluffy language about 'brand' and 'values,' but what really stands out are tangible examples and real impact. Root everything in specifics: what you did, how you did it and what changed as a result."
    }
  ];

  const checklist = [
    "Practiced your 60-sec elevator pitch (and got feedback on it)",
    "Researched the company – product, team, recent updates",
    "Prepared clear motivations for the company and the role",
    "Prepped 2-3 achievements with evidence to back up your statements",
    "Prepared 2-3 strong, specific, well-researched questions to ask at the end",
    "Avoided generic language",
    "Booked time with the Jumpstart in-house interview coach if you want to run a mock or sense-check answers",
    "Send a thank you email to your interviewers after your interview is over"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              First interview: stand out early
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Master the art of first impressions and position yourself as the ideal marketing candidate.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="🎤 Nail your elevator pitch">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                <span className="font-semibold text-gray-800">Start strong</span> — first impressions matter. Your elevator pitch sets the tone for the entire conversation.
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Aim for 45-60 seconds that covers:</h4>
                <div className="grid gap-4">
                  {elevatorPitchElements.map((element, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                        <span className="text-white font-bold text-sm">{i + 1}</span>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{element}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-600 mt-6 font-medium">
                  Focus less on a step-by-step timeline. Instead, tell a story that highlights what drives you and where you shine.
                </p>
              </div>
              
              <InfoCard
                icon={Lightbulb}
                title="Perfect Example"
                description={`"${elevatorPitchExample}"`}
                type="tip"
              />

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Learn from real examples:</h4>
                <VideoGrid videos={elevatorPitchExamples} columns={3} />
              </div>
            </div>
          </StepCard>

          <StepCard title="⭐ How to stand out in your first interview">
            <div className="space-y-10">
              <p className="text-slate-600 text-lg leading-relaxed">
                Standout candidates often do more than just answer questions well — they show initiative, thoughtfulness and energy from day one. You want to make it an easy "yes" for them about you.
              </p>

              <div className="grid gap-8 md:grid-cols-1">
                {standoutTips.map((tip, index) => (
                  <InfoCard
                    key={index}
                    icon={tip.icon}
                    title={tip.title}
                    description={tip.description}
                    accent={true}
                  />
                ))}
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Marketing-specific ideas you could bring:</h4>
                <div className="grid gap-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                      <Lightbulb className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 leading-relaxed">Propose content themes, SEO improvements or event ideas</p>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                      <Target className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 leading-relaxed">Suggest low-cost ways to grow awareness or community</p>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                      <Mic className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-slate-700 leading-relaxed">Come with 2-3 rough ideas for improving positioning or tone of voice</p>
                  </div>
                </div>
                <p className="text-slate-600 mt-6 font-medium">
                  Use real-life examples from their website, product or job description to anchor your suggestions. Even if your ideas aren't perfect, showing that you've thought about the business deeply makes a huge impression.
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="💗 Show you're obsessed with the company">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                It's important to avoid vague statements like "I love your mission" and to show you've done your homework.
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">What you should do instead:</h4>
                <div className="grid gap-4">
                  {companyObsessionTips.map((tip, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#02B491] rounded-xl flex items-center justify-center mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-slate-700 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Better motivation examples:</h4>
                <div className="space-y-6">
                  {motivationExamples.map((example, index) => (
                    <InfoCard
                      key={index}
                      title={`Example ${index + 1}`}
                      description={`"${example.text}"`}
                      type="tip"
                    />
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🎯 Show you can do the job">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Don't just talk about wanting "impact" or "learning." Link back to concrete examples.
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Link back to:</h4>
                <div className="grid gap-4">
                  {jobCapabilityTips.map((tip, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                        <span className="text-white font-bold text-sm">{i + 1}</span>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
              
                                            <InfoCard
                 title="Example"
                 description="In my last role, I launched test campaigns with almost no budget - just Notion pages, Loom videos, and cold DMs. That's why I'm excited by this role - the chance to keep testing and scaling what works."
                 type="tip"
               />
            </div>
          </StepCard>

          <StepCard title="❓ Ask great questions that show you've done your homework">
            <div className="space-y-10">
              <p className="text-slate-600 text-lg leading-relaxed">
                Asking thoughtful questions is a chance to show how you think and great candidates treat interviews as <span className="font-semibold text-gray-800">two-way conversations</span>. They use questions to:
              </p>

              <div className="grid gap-6 md:grid-cols-3">
                {questionTypes.map((type, index) => (
                  <InfoCard
                    key={index}
                    icon={type.icon}
                    title={type.title}
                    description={type.description}
                    accent={true}
                  />
                ))}
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl flex items-center">
                  <XCircle className="w-6 h-6 mr-3 text-red-500" />
                  Things to avoid:
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {badQuestions.map((question, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-1">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">"{question}"</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-emerald-500" />
                  Try asking these instead:
                </h4>
                <div className="space-y-6">
                  {goodQuestions.map((question, index) => (
                    <InfoCard
                      key={index}
                      title={`Example ${index + 1}`}
                      description={`"${question}"`}
                      type="success"
                    />
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🚫 Things to avoid in early interviews">
            <div className="space-y-8">
              <div className="grid gap-6 md:grid-cols-2">
                {avoidTips.map((tip, index) => (
                  <InfoCard
                    key={index}
                    icon={tip.icon}
                    title={tip.title}
                    description={tip.description}
                    type="warning"
                  />
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="✅ First interview checklist">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                When preparing for your 1st round interview, make sure you've:
              </p>
              
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="space-y-4">
                  {checklist.map((item, index) => (
                    <label key={index} className="flex items-start space-x-4 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        className="mt-1.5 rounded border-gray-300 text-[#02B491] focus:ring-[#02B491] focus:ring-offset-0" 
                      />
                      <span className="text-slate-700 leading-relaxed group-hover:text-slate-900 transition-colors">
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-slate-500 text-sm">
                    Feeling stuck or not getting interviews? Reach out to <span className="text-[#02B491] font-medium">info@jumpstart-uk.com</span> for support.
                  </p>
                </div>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingStandOutSection; 